import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  Spinner,
} from '@design-system';
import { useCancellationFeedback } from '../hooks/use-cancellation-feedback';

interface CancellationFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedToCancel: () => void;
}

const CANCELLATION_REASONS = [
  { value: 'didnt-use-enough', label: "I didn't use it enough" },
  { value: 'no-value', label: "I didn't see enough value" },
  { value: 'dont-sell-ltc', label: "I don't sell enough LTC plans" },
  { value: 'too-complex', label: 'Too complex / unclear how to use' },
  { value: 'price-not-right', label: "Price wasn't right for me" },
  { value: 'found-alternative', label: 'I found another solution' },
  { value: 'other', label: 'Other' },
] as const;

export function CancellationFeedbackModal({
  isOpen,
  onClose,
  onProceedToCancel,
}: CancellationFeedbackModalProps) {
  const {
    selectedReason,
    setSelectedReason,
    additionalFeedback,
    setAdditionalFeedback,
    handleSubmit,
    isSubmitDisabled,
    isPending,
  } = useCancellationFeedback({
    onSuccess: () => {
      onClose();
      onProceedToCancel();
    },
  });

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={onClose}
    >
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl">
            Help us improve Waterlily for advisors like you
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            What was the main reason you decided to cancel?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup
            value={selectedReason}
            onValueChange={setSelectedReason}
          >
            <div className="space-y-3">
              {CANCELLATION_REASONS.map(reason => (
                <div
                  key={reason.value}
                  className="flex items-center space-x-2"
                >
                  <RadioGroupItem
                    value={reason.value}
                    id={reason.value}
                  />
                  <Label
                    htmlFor={reason.value}
                    className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {reason.label}
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>

          <div className="space-y-2">
            <Label htmlFor="feedback">
              Anything else you'd like to share?
              {selectedReason === 'other' && <span className="text-sm text-red-500">*</span>}
            </Label>
            <Textarea
              id="feedback"
              placeholder="Your feedback helps us improve..."
              value={additionalFeedback}
              onChange={e => setAdditionalFeedback(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <AlertDialogFooter className="gap-3">
          <AlertDialogCancel
            onClick={e => {
              e.preventDefault();
              handleSubmit();
            }}
            disabled={isSubmitDisabled || isPending}
            className="border-none"
          >
            {isPending ? (
              <>
                <Spinner className="mr-2 size-4" />
                Saving...
              </>
            ) : (
              'Continue to Cancellation'
            )}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onClose}
            disabled={isPending}
          >
            Back
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
